import { type FC } from "react";

import { DraftingSectionModeEnum, type DraftingSourceEnum } from "@app_schema";

import {
  SetupReviewName,
  SetupReviewParent,
  SetupReviewRow,
} from "@styled/setup_review";
import { SourcesPicker } from "@styled/sources_picker";

import { sourceOptions, toggled } from "./drafting_sources";
import {
  type SetupSection,
  sourceNames,
  type useDraftingSetup,
} from "./use_drafting_setup";

type Setup = ReturnType<typeof useDraftingSetup>;

// "Reads: Intake, Records", else "You write this", as the chip reads.
const readsLabel = (sources: readonly DraftingSourceEnum[]) => {
  const names = sourceNames(sources);
  return names ? `Reads: ${names}` : "You write this";
};

// One proposed section: kept or not, its name, and what it reads: his to write
// while it reads nothing.
export const DraftingSetupRow: FC<{ section: SetupSection; setup: Setup }> = ({
  section,
  setup,
}) => {
  const choice = setup.choiceOf(section);

  return (
    <>
      {section.groupLabel && (
        <SetupReviewParent>{section.groupLabel}</SetupReviewParent>
      )}
      <SetupReviewRow
        label={choice.name}
        checked={choice.checked}
        onCheck={() => setup.change(section, { checked: !choice.checked })}
        selected={setup.selected === section.block}
        onSelect={() => setup.select(section.block)}
        nested={section.level > 1}
        line={section.line}
        name={
          <SetupReviewName
            value={choice.name}
            onChange={(name) => setup.change(section, { name })}
          />
        }
        reads={
          <SourcesPicker
            label={readsLabel(choice.sources)}
            title={`${choice.name} reads from`}
            options={sourceOptions(choice.sources)}
            onToggle={(value) =>
              setup.change(section, {
                sources: toggled(choice.sources, value as DraftingSourceEnum),
              })
            }
            footer={
              <>
                {section.mode === DraftingSectionModeEnum.His && (
                  <p>Leave all unticked and you write this section yourself.</p>
                )}
                <p>Your own draft text is always read.</p>
              </>
            }
          />
        }
      />
    </>
  );
};
